import { AgenturNameCell } from 'app/components/shared/AgenturName.cell';
import { Table } from 'app/components/table/table';
import { AggregrationType, ITableColumn, TableModel, TableRow } from 'app/components/table/table.model';
import { useStore } from 'app/context';
import { observer } from 'mobx-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useHistory } from 'react-router';
import colors from 'assets/scss/colors.module.scss';
import { Icon } from 'app/components/common/Icon';
import { GoogleProfileMultiGp26VM } from 'app/stores/ui/agt.googleprofile.ui.store';
import { AgtGoogleProfileDetail } from './AgtGoogleProfileDetail';
import { init, EChartsOption, ECharts } from 'echarts';
import { debounce } from 'lodash';
import { formatBM } from 'app/utils';

interface IGoogleProfileMonthlyChart {
	data: { month: string; count: number }[];
	gupZiel1: number | null;
	gupZiel2: number | null;
}

const GoogleProfileMonthlyChart = observer((props: IGoogleProfileMonthlyChart) => {
	const { uiStore } = useStore();
	const { data, gupZiel1, gupZiel2 } = props;
	const chartRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!data || data.length === 0) return;

		const dataMax = Math.max(...data.map((d) => d.count));
		const max = Math.max(dataMax, gupZiel2 ?? 0, gupZiel1 ?? 0, 1);
		const yAxisMax = Math.ceil(max * 1.1);

		// Build markLines for reference lines
		const markLineData: any[] = [];
		if (gupZiel1 != null) {
			markLineData.push({
				yAxis: gupZiel1,
				label: {
					formatter: `Ziel 1: ${gupZiel1}`,
					position: 'insideEndTop',
					fontSize: 11,
					color: colors.grey,
				},
				lineStyle: {
					color: colors.grey,
					type: [4, 2],
				},
			});
		}
		if (gupZiel2 != null) {
			markLineData.push({
				yAxis: gupZiel2,
				label: {
					formatter: `Ziel 2: ${gupZiel2}`,
					position: 'insideEndTop',
					fontSize: 11,
					color: colors.grey,
				},
				lineStyle: {
					color: colors.grey,
					type: [4, 2],
				},
			});
		}

		const option: EChartsOption = {
			tooltip: {
				trigger: 'axis',
				formatter: (params: any) => {
					if (Array.isArray(params) && params[0]) {
						const p = params[0];
						return `${p.name}<br/>${p.value} Teilhaber`;
					}
					return '';
				},
			},
			xAxis: {
				type: 'category',
				data: data.map((d) => d.month),
				axisLabel: {
					fontSize: 12,
				},
				name: 'Monat',
				nameLocation: 'middle',
				nameGap: 25,
				nameTextStyle: {
					fontSize: 12,
				},
			},
			yAxis: {
				type: 'value',
				max: yAxisMax,
				axisLabel: {
					fontSize: 12,
				},
				name: 'Agenturen',
				nameLocation: 'middle',
				nameGap: 40,
				nameTextStyle: {
					fontSize: 12,
				},
			},
			series: [
				{
					type: 'bar',
					data: data.map((d) => ({
						value: d.count,
						itemStyle: {
							color: gupZiel1 != null && d.count >= gupZiel1 ? colors.green : colors.primary,
						},
					})),
					markLine: {
						data: markLineData,
						symbol: 'none',
					},
				},
			],
			grid: {
				left: '60px',
				right: '40px',
				top: '40px',
				bottom: '50px',
			},
			animation: !uiStore.printMode,
		};

		let chart: ECharts | null = null;
		if (chartRef.current) {
			chart = init(chartRef.current, null, { renderer: 'svg' });
			chart.setOption(option);
		}

		const handleResize = debounce(() => {
			chart?.resize();
		}, 300);
		window.addEventListener('resize', handleResize);

		return () => {
			chart?.dispose();
			window.removeEventListener('resize', handleResize);
		};
	}, [data, gupZiel1, gupZiel2, uiStore.printMode]);

	return <div ref={chartRef} style={{ width: '100%', height: 300 }} />;
});

export const AgtGoogleProfileMultiGp26Liste = observer(() => {
	const { berichteUiiStore, agtGoogleProfileUiStore, agtMultiGpUiStore, agenturListUiStore, agenturFilterUiStore, session } = useStore();
	const location = useLocation();
	const history = useHistory();
	const [hasItems, setHasItems] = useState(false);
	const [monthlyData, setMonthlyData] = useState<{ month: string; count: number }[]>([]);
	const [gupZiel1, setGupZiel1] = useState<number | null>(null);
	const [gupZiel2, setGupZiel2] = useState<number | null>(null);

	const selectedAgtId = useMemo(() => {
		const params = new URLSearchParams(location.search);
		const detailAgtId = params.get('detailAgtId');
		return detailAgtId ? parseInt(detailAgtId) : null;
	}, [location.search]);

	const tm = useMemo(() => {
		const tm = new TableModel<GoogleProfileMultiGp26VM, number>();
		const cols: ITableColumn<GoogleProfileMultiGp26VM, number>[] = [];

		cols.push({
			label: 'Agentur',
			path: 'agt.name',
			sortBy: 'agt.name',
			render: AgenturNameCell,
		});

		cols.push({
			label: 'Teilhaber',
			path: 'agt.thCount',
			sortBy: 'agt.thCount',
		});

		cols.push({
			label: 'Bewertungen',
			path: 'bew',
			agg: [{ aggType: AggregrationType.sum }],
		});

		cols.push({
			label: 'Monate ≥ 3 Neue Bew.',
			path: 'neueBewGreater3MonthCount',
			agg: [{ aggType: AggregrationType.sum }],
		});

		tm.sortBy = 'agt.name';
		tm.setCols(cols);
		tm.idProperty = 'agtId';
		tm.idType = 'number';

		tm.onRowClick = (row: TableRow<GoogleProfileMultiGp26VM>) => {
			const params = new URLSearchParams(history.location.search);
			params.set('detailAgtId', row.data.agtId.toString());
			history.replace(history.location.pathname + '?' + params.toString());
		};

		return tm;
	}, []);

	useEffect(() => {
		Promise.all([agtGoogleProfileUiStore.loadMultiGp26(), agtMultiGpUiStore.load()]).then(([res]) => {
			let items = agenturListUiStore.applyFilterOnAgt(res) as GoogleProfileMultiGp26VM[];
			if (berichteUiiStore.selectedAgt) {
				const agtId = berichteUiiStore.selectedAgt.agtId;
				items = items.filter((i) => i.agt.agtId === agtId);
			}

			setHasItems(items.length > 0);
			tm.setRowData(items);
			tm.sort();

			// Build monthly distribution: count of agencies with >=3 neue Bew per month of 2026
			const monthlyCounts = new Map<number, number>();
			for (const item of items) {
				for (const bm of item.neueBewGreater3Bms) {
					monthlyCounts.set(bm, (monthlyCounts.get(bm) || 0) + (item.agt.thCount ? item.agt.thCount : 1));
				}
			}
			const monthly: { month: string; count: number }[] = [];
			for (let m = 1; m <= 12; m++) {
				const bm = 202600 + m;
				monthly.push({ month: formatBM(bm), count: monthlyCounts.get(bm) || 0 });
			}
			setMonthlyData(monthly);

			// Resolve VD for Ziele: user's VD (multi) or VD filter
			let vdId: string | undefined;
			const vdFilter = agenturFilterUiStore._filters.find((f) => f.name === 'vd');
			if (vdFilter && vdFilter.hasValue && vdFilter.value.length === 1) {
				vdId = vdFilter.value[0];
			} else if (session.isMulti && session.currentUser?.vdList?.length) {
				vdId = session.currentUser.vdList[0];
			}

			if (vdId) {
				const multiGp = agtMultiGpUiStore.items.find((m) => m.vd === vdId);
				if (multiGp) {
					setGupZiel1(multiGp.gupThreshold1);
					setGupZiel2(multiGp.gupThreshold2);
				}
			} else {
				setGupZiel1(null);
				setGupZiel2(null);
			}
		});
	}, [tm, agtGoogleProfileUiStore, agtMultiGpUiStore, berichteUiiStore.selectedAgt, agenturListUiStore.filterHasChanged]);

	const handleCloseDetail = useCallback(() => {
		const params = new URLSearchParams(history.location.search);
		params.delete('detailAgtId');
		const newSearch = params.toString();
		history.replace(history.location.pathname + (newSearch ? '?' + newSearch : ''));
	}, [history]);

	return (
		<>
			<div className="table-container">
				{hasItems && monthlyData.length > 0 && (
					<div className="pad-1rem pad-top-3rem">
						<div className="is-centered">Anzahl Agenturen mit {'≥'} 3 neuen Bewertungen je Monat 2026</div>
						<GoogleProfileMonthlyChart data={monthlyData} gupZiel1={gupZiel1} gupZiel2={gupZiel2} />
					</div>
				)}

				{!hasItems ? <div className="pad-1rem">Keine Daten vorhanden</div> : <Table stickyHeader={true} tm={tm} stickyFooter={true} />}
			</div>
			{selectedAgtId && <AgtGoogleProfileDetail agtId={selectedAgtId} onClose={handleCloseDetail} />}
		</>
	);
});
